import React from 'react';
import styled from 'styled-components';
import {
  AssessmentTestSessionDetailResponse,
  AssessmentTestStep,
  AssessmentTestStepAnswer,
} from '../../../api/admin';
import CollapsibleSection, {
  CollapsibleSectionHeader,
  CollapsibleSectionLabel,
} from '../CollapsibleSection';
import { SectionWrapper, SectionHeader, Title, Caption } from './common';
import SmallMarkdown from '../../Markdown/SmallMarkdown';
import {
  QuestionStatusBadge,
  getQuestionStatus,
} from './StepQuestionsResults/QuestionStatusCell';
import { typeLabels } from './StepQuestionsResults/QuestionTypeCell';
import { formatSectionAnswer } from './answerFormatting';

/**
 * Eval-independent panel that lists, per step and per question, the question
 * prompt and the learner's verbatim submitted answer. Renders for
 * completed-but-unscored sessions (does NOT gate on `reviewedAt`/`results`) so
 * reviewers can read submissions before scoring is available.
 */
export function ResultsRawAnswers({
  test,
}: {
  test: AssessmentTestSessionDetailResponse | null | undefined;
}) {
  const steps = test?.steps ?? [];
  const didStartTest = test?.startedAt != null;

  const [expandedStepIds, setExpandedStepIds] = React.useState<Array<string>>(
    () => steps.map((step) => step.id)
  );

  if (steps.length === 0) {
    return null;
  }

  return (
    <SectionWrapper>
      <SectionHeader>
        <Title>Submitted Answers</Title>
        <Caption>The learner&apos;s answers as submitted, per question</Caption>
      </SectionHeader>
      {steps.map((step) => {
        const answer = test?.answers.find((a) => a.id === step.id);
        const isExpanded = expandedStepIds.includes(step.id);
        return (
          <CollapsibleSection
            key={step.id}
            headerLeft={
              <CollapsibleSectionHeader>{step.title}</CollapsibleSectionHeader>
            }
            headerRight={
              <CollapsibleSectionLabel>{step.label}</CollapsibleSectionLabel>
            }
            expanded={isExpanded}
            onToggle={() =>
              setExpandedStepIds((prev) =>
                prev.includes(step.id)
                  ? prev.filter((id) => id !== step.id)
                  : [...prev, step.id]
              )
            }
          >
            <StepAnswers
              step={step}
              answer={answer}
              didStartTest={didStartTest}
            />
          </CollapsibleSection>
        );
      })}
    </SectionWrapper>
  );
}

function StepAnswers({
  step,
  answer,
  didStartTest,
}: {
  step: AssessmentTestStep;
  answer: AssessmentTestStepAnswer | undefined;
  didStartTest: boolean;
}) {
  if (step.sections.length === 0) {
    return (
      <AnswersContainer>
        <EmptyText>No questions in this step</EmptyText>
      </AnswersContainer>
    );
  }

  return (
    <AnswersContainer>
      {step.sections.map((section, index) => {
        const sectionAnswer =
          answer?.sections.find((s) => s.id === section.id) ?? null;
        const { status, iconClass } = getQuestionStatus({
          isAnswered: !!sectionAnswer?.isAnswered,
          isInteracted: !!sectionAnswer?.didInteract,
          didStartTest,
          hasAnswer: !!sectionAnswer,
        });
        const answerText = formatSectionAnswer(sectionAnswer, section);
        const typeLabel = typeLabels[section.type] ?? section.type;
        const followUpAnswer = sectionAnswer?.followUpAnswer;
        const uniqueId = `raw-answer-status-${step.id}-${section.id}`;

        return (
          <QuestionBlock key={section.id}>
            <QuestionMeta>
              <QuestionNumber>Question {index + 1}</QuestionNumber>
              <QuestionStatusBadge
                status={status}
                iconClass={iconClass}
                uniqueId={uniqueId}
              />
              <TypePill>{typeLabel}</TypePill>
            </QuestionMeta>
            <FieldLabel>Question</FieldLabel>
            <QuestionMarkdown md={section.content || '-'} />
            <FieldLabel style={{ marginTop: 12 }}>Answer</FieldLabel>
            {answerText.trim() ? (
              <AnswerMarkdown md={answerText} />
            ) : (
              <EmptyText>No answer submitted</EmptyText>
            )}
            {section.followUpContent && (
              <>
                <FieldLabel style={{ marginTop: 12 }}>
                  Follow-up Answer
                </FieldLabel>
                {followUpAnswer && followUpAnswer.trim() ? (
                  <AnswerMarkdown md={followUpAnswer} />
                ) : (
                  <EmptyText>No follow-up answer submitted</EmptyText>
                )}
              </>
            )}
          </QuestionBlock>
        );
      })}
    </AnswersContainer>
  );
}

const AnswersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.bgActive};
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  padding: 24px;
`;

const QuestionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.bgPage};
`;

const QuestionMeta = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`;

const QuestionNumber = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const TypePill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 12px;
  white-space: nowrap;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const FieldLabel = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textRegular};
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
  opacity: 0.8;
`;

const QuestionMarkdown = styled(SmallMarkdown)`
  * {
    font-size: 13px !important;
    color: ${({ theme }) => theme.colors.textRegular};
  }
`;

const AnswerMarkdown = styled(SmallMarkdown)`
  * {
    font-size: 13px !important;
    color: ${({ theme }) => theme.colors.textActive};
  }
`;

const EmptyText = styled.div`
  font-size: 13px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.textRegular};
  opacity: 0.7;
`;
